import { useForm } from 'react-hook-form';
import {
  EnableMfaRequestMfaTypeEnum,
  MfaControllerApi,
} from '../../openapi/keymaker';
import ErrorService from '../../services/ErrorService';
import { fetchKeymakerCurrentUser, mfaSignIn } from '../../slices/AuthSlice';
import { showSuccessToast } from '../../slices/ToastNotificationSlice';
import { useAppDispatch } from '../../slices/store';
import { getCommonsApiErrorMessage } from '../../utils/ErrorUtils';
import { getKeymakerConfiguration } from '../../utils/OpenapiConfigurationUtils';
import { formatPhoneNumber } from '../../utils/StringUtils';
import SidebarModalForm from '../Modals/SidebarModalForm';
import ControlledTextInput from '../inputs/ControlledTextInput';

interface FormData {
  code: number;
}

interface SMSVerificationFormProps {
  isOpen: boolean;
  onClose(): void;
  verificationPhoneNumber: string;
  setIsMFASetupModalOpen(enable: boolean): void;
  isForceMfa?: boolean;
}

const SMSVerificationForm: React.FC<SMSVerificationFormProps> = ({
  isOpen,
  onClose,
  verificationPhoneNumber,
  setIsMFASetupModalOpen,
  isForceMfa,
}) => {
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<FormData>();

  const onSubmit = async (values: FormData) => {
    try {
      await new MfaControllerApi(await getKeymakerConfiguration()).enableMfa({
        code: values.code,
        mfaType: EnableMfaRequestMfaTypeEnum.Sms,
        phoneNumber: verificationPhoneNumber,
      });
      if (isForceMfa) {
        await dispatch(mfaSignIn(values.code));
      }
      await dispatch(fetchKeymakerCurrentUser());
      setIsMFASetupModalOpen(false);
      onClose();
      dispatch(
        showSuccessToast('Successfully enabled two factor authentication.'),
      );
    } catch (e: any) {
      setError('code', {
        message: getCommonsApiErrorMessage(e),
      });
      ErrorService.notify('Unable to enable two factor authentication', e, {
        data: {
          code: values.code,
          mfaType: EnableMfaRequestMfaTypeEnum.Sms,
          phoneNumber: verificationPhoneNumber,
        },
      });
    }
  };

  return (
    <SidebarModalForm
      isOpen={isOpen}
      onClose={onClose}
      isSubmitting={isSubmitting}
      title='Two-Factor Authentication: Text Messages (SMS)'
      subtitle='Receive verification codes via text messages'
      onSubmit={handleSubmit(onSubmit)}
      actionTitle='Verify Code'
      cancelTitle='Back'
      isEqualWidth
    >
      <div>
        <p className='font-inter'>Enter authentication code</p>
        <p className='font-inter text-sm mt-2'>
          {`We just sent you a 6-digit code to ${formatPhoneNumber(
            verificationPhoneNumber,
          )}`}
        </p>
        <div className='mt-5'>
          <ControlledTextInput<FormData, 'code'>
            control={control}
            name='code'
            type='number'
            placeholder='123456'
            rules={{
              required: 'Please enter authentication code',
              minLength: {
                value: 6,
                message: 'Must have 6 digit number',
              },
              maxLength: {
                value: 6,
                message: 'Must have 6 digit number',
              },
            }}
          />
        </div>
      </div>
    </SidebarModalForm>
  );
};

export default SMSVerificationForm;
